import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '@/shell/AppShell'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeAIButton } from '@/shell/ForgeAIButton'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { Field } from '@/components/forge/Field'
import { Chip, Segmented, SegmentedItem } from '@/components/forge/Chip'
import { Icon } from '@/components/forge/Icon'
import { PlaceFilter } from '@/components/forge/PlaceFilter'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { TablePagination } from '@/components/forge/TablePagination'
import { Num } from '@/components/forge/primitives'
import {
  DEFAULT_PAGE_SIZE,
  LOGBOOK_CATEGORIES,
  LOGBOOK_SEVERITIES,
  LOGBOOK_VIEWS,
} from '@/features/logbook/api'
import {
  useCreateLogbookEntry,
  useLogbook,
  useLogbookAccess,
  useLogbookListStream,
} from '@/features/logbook/queries'
import { trioOf } from '@/features/hierarchy/place'
import { useShiftPatterns } from '@/features/shiftPatterns/queries'
import { usePermission } from '@/features/auth/permissions'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { formatInteger } from '@/lib/format'
import { LogbookFormDialog } from './LogbookFormDialog'
import { LogbookEntry } from './LogbookEntry'
import { LogbookTimeline } from './LogbookTimeline'

/**
 * Logbook. LIVE.
 *
 * What the crew wrote down, which is the evidence sensors cannot produce.
 *
 * ── Everything that narrows the log is in the URL ─────────────────────────
 * `?view=timeline&category=fault&severity=high&asset=…&shift=…&from=…&to=…`.
 * The prototype keeps the view and the filters in `useState`, so a night
 * somebody wants a colleague to read cannot be linked to. The `shift` param is
 * the destination the Shifts detail panel's Logbook button points at.
 *
 * ── The counts come down with the rows ────────────────────────────────────
 * `counts.category` and `counts.severity` are computed server-side over the
 * scoped board BEFORE those two filters apply, so each chip carries the number
 * it would narrow to and pressing one does not rewrite the row above it. Same
 * arrangement as the work-order board's column headers, and the reason there is
 * no second request for a rollup that could disagree with the feed.
 *
 * ── Four pickers, from the resources that own them ────────────────────────
 * The place filter drills the hierarchy, the shift filter reads Shift Master,
 * and the dialog's two link fields read the anomaly register and the work-order
 * collection. None of them rides on the logbook response, because the API does
 * not put them there — and folding the whole estate into every page of a feed
 * would be the wrong shape even if it did. Each is fetched only when something
 * that needs it is actually open.
 */
export function LogbookPage() {
  const { t } = useTranslation(['app', 'common'])
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()

  const access = useLogbookAccess()

  const view = LOGBOOK_VIEWS.includes(searchParams.get('view')) ? searchParams.get('view') : 'feed'
  const shiftId = searchParams.get('shift') ?? null
  const category = LOGBOOK_CATEGORIES.includes(searchParams.get('category'))
    ? searchParams.get('category')
    : 'all'
  const severity = LOGBOOK_SEVERITIES.includes(searchParams.get('severity'))
    ? searchParams.get('severity')
    : 'all'
  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  const page = Number(searchParams.get('page')) || 1

  const [composing, setComposing] = useState(false)
  /* A site, line or asset, through `PlaceFilter`. It replaced a select of every
     machine, which read the whole estate to fill and could not reach a site or
     a line. State rather than the URL, as on the anomaly list: the picker's
     value carries a name and a trail an id alone cannot rebuild. */
  const [place, setPlace] = useState(null)

  useDocumentTitle(`${t('logbook.title')} · ${t('common:brand.name')}`)

  /*
   * ── The DEMO search, and why there is no box any more ──────────────────
   * The demo collection took `q`, expanded it over a table of the words a plant
   * uses for one thing, and answered with the terms that earned a match — which
   * is what made the design's violet "semantic search" marker honest rather
   * than decorative.
   *
   * `list_logbook_entries` has no search param at all. Not `q`, not `search`,
   * unlike `list_work_orders` which has the latter. An unrecognised param is
   * IGNORED by FastAPI rather than rejected, so leaving the box in place would
   * have it return the unfiltered page 1 under a heading claiming otherwise —
   * a search that silently finds everything is worse than no search.
   *
   * Filtering the page in the client is the other tempting wrong answer: it
   * would search the fifty rows that happen to be loaded and quietly report
   * nothing on page two. Recorded in BACKEND-GAPS.md; restoring it means a
   * `search` param server-side, at which point this block and the `q` handling
   * in `fetchLogbook` come back.
   *
   * const [text, setText] = useState(query)
   * useEffect(() => { … debounce into ?q= … }, [text, query, setSearchParams])
   *
   * ── And the cited-only chip ───────────────────────────────────────────
   * `cited` meant "an investigation cited this note", computed by the fixture
   * from Forge AI's own tool call run backwards. There is no such column. The
   * nearest real fact is `anomaly_id`, which is the link the WRITER made — a
   * weaker claim, already shown on the card, and not one worth a chip that
   * would read as the old one.
   *
   * <Chip selected={cited} onClick={() => setParam('cited', cited ? null : '1')}>
   *   <Icon name="ai" size="xs" />
   *   {t('logbook.citedOnly')}
   * </Chip>
   */

  const params = useMemo(
    () => ({
      category,
      severity,
      ...(trioOf(place) ?? {}),
      shiftId: shiftId ?? undefined,
      from: from || undefined,
      to: to || undefined,
      page,
      pageSize: DEFAULT_PAGE_SIZE,
    }),
    [category, severity, place, shiftId, from, to, page],
  )

  const logbook = useLogbook(params, { enabled: access.granted })
  /* Live: any entry written, edited or re-linked in the reader's scope
     refetches the page on screen. A delete is not announced — see
     `features/logbook/liveStream.js`. */
  useLogbookListStream({ enabled: access.granted })
  const create = useCreateLogbookEntry()

  const entries = logbook.data?.items ?? []
  const counts = logbook.data?.counts ?? { category: {}, severity: {} }

  /* Shift Master's templates — the same rows `shift_id` is a foreign key into.
     Live and behind `shifts.manage`, which the logbook does not imply,
     so a failure here is stated rather than drawn as an empty select. */
  const patterns = useShiftPatterns()
  const shifts = patterns.data?.items ?? []

  /* The dialog's two link pickers, each behind its own permission. The lists
     themselves are read by `LinkPickerDialog`, at the entry's place. */
  const canPickAnomaly = usePermission('anomalies.manage').granted
  const canPickWorkOrder = usePermission('work_orders.manage').granted
  const canReadSensors = usePermission('sensors.manage_sensors').granted

  const setParam = (key, value) =>
    setSearchParams((previous) => {
      const url = new URLSearchParams(previous)
      if (value) url.set(key, value)
      else url.delete(key)
      /* Any change to what is being looked at returns to page 1. Landing on
         page 4 of a narrower result is how a filter looks broken. */
      if (key !== 'page' && key !== 'view') url.delete('page')
      return url
    })

  const narrowed =
    category !== 'all' || severity !== 'all' || Boolean(place) || Boolean(shiftId) || Boolean(from) || Boolean(to)

  const clear = () => {
    setPlace(null)
    setSearchParams((previous) => {
      const url = new URLSearchParams(previous)
      for (const key of ['category', 'severity', 'asset', 'shift', 'from', 'to', 'page']) {
        url.delete(key)
      }
      return url
    })
  }

  return (
    <Page>
      <header className="mb-4 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 className="m-0 text-display text-balance">{t('logbook.title')}</h1>
          </div>
          <p className="mt-2 mb-0 text-body text-ink-dim">{t('logbook.subtitle')}</p>
        </div>

        <Segmented label={t('logbook.viewLabel')}>
          {LOGBOOK_VIEWS.map((entry) => (
            <SegmentedItem
              key={entry}
              selected={view === entry}
              label={t(`logbook.view.${entry}`)}
              onClick={() => setParam('view', entry === 'feed' ? null : entry)}
              className="w-auto px-3 text-body-sm font-semibold"
            >
              {t(`logbook.view.${entry}`)}
            </SegmentedItem>
          ))}
        </Segmented>

        <ForgeAIButton action="createLogbookEntry" disabled={!access.granted} />
        <ForgeButton
          variant="primary"
          onClick={() => setComposing(true)}
          disabled={!access.granted || composing}
        >
          <Icon name="plus" size="xs" />
          {t('logbook.newEntry')}
        </ForgeButton>
      </header>

      {/* ── The two chip rows ────────────────────────────────────────────
          Each chip carries its own count, from `counts`. A category with no
          entries still appears, greyed by its zero rather than hidden: a
          filter list that changes shape as the log fills is one nobody can
          learn, and "Safety 0" is a useful thing to be able to read. */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <Chip selected={category === 'all'} onClick={() => setParam('category', null)}>
          {t('logbook.allCategories')}
        </Chip>
        {LOGBOOK_CATEGORIES.map((entry) => (
          <Chip
            key={entry}
            selected={category === entry}
            onClick={() => setParam('category', category === entry ? null : entry)}
          >
            {t(`logbook.category.${entry}`)}{' '}
            <Num className="text-ink-dim">{formatInteger(counts.category[entry] ?? 0)}</Num>
          </Chip>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Chip selected={severity === 'all'} onClick={() => setParam('severity', null)}>
          {t('logbook.allSeverities')}
        </Chip>
        {LOGBOOK_SEVERITIES.map((entry) => (
          <Chip
            key={entry}
            selected={severity === entry}
            onClick={() => setParam('severity', severity === entry ? null : entry)}
          >
            {t(`common:severity.${entry}`)}{' '}
            <Num className="text-ink-dim">{formatInteger(counts.severity[entry] ?? 0)}</Num>
          </Chip>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <PlaceFilter
          id="logbook-place"
          value={place}
          labelAbove
          onChange={(next) => {
            setPlace(next)
            setParam('page', null)
          }}
        />

        {patterns.isError ? (
          <p className="m-0 text-body-sm text-on-amber">{t('logbook.shiftsFailed')}</p>
        ) : (
          <ForgeSelect
            label={t('logbook.shiftFilterLabel')}
            size="sm"
            value={shiftId ?? 'all'}
            onValueChange={(value) => setParam('shift', value === 'all' ? null : value)}
            options={[
              { value: 'all', label: t('logbook.anyShift') },
              ...shifts.map((shift) => ({ value: shift.id, label: shift.name })),
            ]}
          />
        )}

        {/* `shift_date_from`/`shift_date_to`, inclusive at both ends server-side.
            Two date inputs and not a range picker, because the wire is two
            independent params and either may be set alone. */}
        <Field
          size="sm"
          label={t('logbook.from')}
          type="date"
          value={from}
          onChange={(event) => setParam('from', event.target.value || null)}
        />
        <Field
          size="sm"
          label={t('logbook.to')}
          type="date"
          value={to}
          onChange={(event) => setParam('to', event.target.value || null)}
        />

        {narrowed ? (
          <ForgeButton variant="ghost" size="sm" onClick={clear}>
            {t('logbook.clear')}
          </ForgeButton>
        ) : null}

        <span className="ms-auto text-body-sm text-ink-dim">
          <Num>{t('logbook.count', { total: formatInteger(logbook.data?.total ?? 0) })}</Num>
        </span>
      </div>

      {access.isPending ? (
        <SkeletonRows count={4} />
      ) : !access.granted ? (
        <EmptyState icon="lock" title={t('logbook.forbidden')} body={t('logbook.forbiddenBody')} />
      ) : logbook.isError ? (
        <ErrorState
          error={logbook.error}
          title={t('logbook.loadFailed')}
          onRetry={() => logbook.refetch()}
        />
      ) : logbook.isPending ? (
        <SkeletonRows count={4} />
      ) : entries.length === 0 ? (
        <EmptyState
          icon="doc"
          title={narrowed ? t('logbook.emptySearchTitle') : t('logbook.emptyTitle')}
          body={narrowed ? t('logbook.emptySearchBody') : t('logbook.emptyBody')}
          action={
            narrowed ? (
              <ForgeButton onClick={clear}>{t('logbook.clear')}</ForgeButton>
            ) : (
              <ForgeButton variant="primary" onClick={() => setComposing(true)}>
                {t('logbook.newEntry')}
              </ForgeButton>
            )
          }
        />
      ) : (
        <>
          {view === 'timeline' ? (
            <LogbookTimeline entries={entries} />
          ) : (
            <div className="flex flex-col gap-3">
              {entries.map((entry) => (
                <LogbookEntry key={entry.id} entry={entry} />
              ))}
            </div>
          )}

          {/* Paged, where the demo answered the whole log in one response. A
              logbook only grows, so a feed with no pager is one that gets
              slower every shift until somebody notices. */}
          <TablePagination
            className="mt-2"
            total={logbook.data?.total ?? 0}
            page={logbook.data?.page ?? page}
            pageSize={logbook.data?.pageSize ?? DEFAULT_PAGE_SIZE}
            label={t('logbook.title')}
            onPageChange={(next) => setParam('page', next > 1 ? String(next) : null)}
          />
        </>
      )}

      {composing ? (
        <LogbookFormDialog
          /* Mounted only while it is open, so it starts empty every time
             without an effect resetting anything. `WorkOrderFormDialog` needs
             a `key` because it stays mounted across two different orders; this
             one has nothing to distinguish openings by, and unmounting does
             the same job. */
          open={composing}
          shifts={shifts}
          shiftsFailed={patterns.isError}
          canPickAnomaly={canPickAnomaly}
          canPickWorkOrder={canPickWorkOrder}
          canReadSensors={canReadSensors}
          pending={create.isPending}
          onClose={() => setComposing(false)}
          onSubmit={(payload) =>
            create.mutate(payload, {
              onSuccess: () => {
                setComposing(false)
                toast(t('logbook.toast.saved'))
              },
              onError: () => toast(t('logbook.toast.failed'), 'error'),
            })
          }
        />
      ) : null}
    </Page>
  )
}
